function isObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

let localIdCounter = 0;

function createLocalId(prefix) {
    localIdCounter += 1;
    const randomPart = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${localIdCounter}`;
    return `${prefix}-${randomPart}`;
}

export function normalizeText(value) {
    if (typeof value === 'string') {
        return value;
    }
    if (value == null) {
        return '';
    }
    return String(value);
}

export function normalizeRole(role) {
    const normalizedRole = normalizeText(role).trim().toLowerCase();
    if (normalizedRole === 'system') {
        return 'system';
    }
    if (normalizedRole === 'tool') {
        return 'tool';
    }
    if (normalizedRole === 'assistant' || normalizedRole === 'agent') {
        return 'assistant';
    }
    return 'user';
}

function normalizeMessageStatus(rawStatus, role) {
    const normalizedStatus = normalizeText(rawStatus).trim().toLowerCase();
    if (normalizedStatus) {
        if (normalizedStatus === 'complete') {
            return 'completed';
        }
        return normalizedStatus;
    }

    return role === 'tool' ? 'start' : 'completed';
}

export function normalizeTimestamp(value) {
    if (!value) {
        return new Date().toISOString();
    }

    if (typeof value === 'number') {
        return new Date(value).toISOString();
    }

    if (typeof value?.toDate === 'function') {
        return value.toDate().toISOString();
    }

    const asDate = new Date(value);
    if (Number.isNaN(asDate.getTime())) {
        return new Date().toISOString();
    }

    return asDate.toISOString();
}

export function extractConversationEnvelope(payload = {}) {
    if (!isObject(payload)) {
        return {};
    }

    return payload.data && isObject(payload.data)
        ? payload.data
        : payload;
}

export function normalizeMessage(rawMessage = {}, fallback = {}) {
    const messageObject = isObject(rawMessage) ? rawMessage : {};
    const stringMessage = typeof rawMessage === 'string' ? rawMessage : '';
    const metadata = isObject(messageObject?.metadata) ? messageObject.metadata : {};
    const content = isObject(messageObject?.content) ? messageObject.content : {};
    const stringContent = typeof messageObject?.content === 'string' ? messageObject.content : '';
    const normalizedRole = normalizeRole(
        messageObject?.role || messageObject?.senderRole || messageObject?.type || fallback?.role || (stringMessage ? 'assistant' : 'user')
    );
    const primaryText = stringMessage || messageObject?.text || messageObject?.message;
    const text = normalizeText(
        primaryText
        ?? stringContent
        ?? content?.message
        ?? content?.text
        ?? metadata?.label
        ?? fallback?.text
    ).trimEnd();

    return {
        id: messageObject?.id || messageObject?.messageId || fallback?.id || createLocalId('message'),
        role: normalizedRole,
        text,
        createdAt: normalizeTimestamp(messageObject?.createdAt || messageObject?.timestamp || fallback?.createdAt),
        updatedAt: normalizeTimestamp(messageObject?.updatedAt || messageObject?.createdAt || messageObject?.timestamp || fallback?.updatedAt || fallback?.createdAt),
        runId: messageObject?.runId || metadata?.runId || messageObject?.run?.id || fallback?.runId || null,
        status: normalizeMessageStatus(
            messageObject?.status || metadata?.status || fallback?.status,
            normalizedRole,
        ),
        metadata: {
            ...metadata,
            class: messageObject?.class || metadata?.class || fallback?.metadata?.class,
            kind: metadata?.kind || fallback?.metadata?.kind || null,
            toolName: metadata?.toolName || fallback?.metadata?.toolName || null,
            display: metadata?.display || fallback?.metadata?.display || null,
            attempt: metadata?.attempt || fallback?.metadata?.attempt || null,
            pipeline: metadata?.pipeline || messageObject?.pipeline || fallback?.metadata?.pipeline || null,
            format: metadata?.format || messageObject?.format || fallback?.metadata?.format || null,
            intent: metadata?.intent || messageObject?.intent || fallback?.metadata?.intent || null,
            label: metadata?.label || fallback?.metadata?.label || null,
            source: fallback?.metadata?.source || metadata?.source || 'persisted',
        },
    };
}

export function normalizeRun(rawRun = {}, fallback = {}) {
    const metadata = isObject(rawRun?.metadata) ? rawRun.metadata : {};
    return {
        id: rawRun?.id || rawRun?.runId || fallback?.id || createLocalId('run'),
        conversationId: rawRun?.conversationId || metadata?.conversationId || fallback?.conversationId || null,
        status: rawRun?.status || fallback?.status || 'queued',
        phase: rawRun?.phase || fallback?.phase || rawRun?.status || 'queued',
        selectedPipeline: rawRun?.selectedPipeline || metadata?.selectedPipeline || fallback?.selectedPipeline || null,
        outputMessageId: rawRun?.outputMessageId || metadata?.outputMessageId || fallback?.outputMessageId || null,
        error: rawRun?.error || fallback?.error || null,
        createdAt: normalizeTimestamp(rawRun?.createdAt || fallback?.createdAt),
        updatedAt: normalizeTimestamp(rawRun?.updatedAt || rawRun?.createdAt || fallback?.updatedAt),
    };
}

export function normalizeConversationSnapshot(snapshot = {}) {
    const envelope = extractConversationEnvelope(snapshot);
    const conversation = isObject(envelope?.conversation) ? envelope.conversation : envelope;
    const messages = Array.isArray(envelope?.messages)
        ? envelope.messages
        : Array.isArray(conversation?.messages)
            ? conversation.messages
            : [];
    const runs = Array.isArray(envelope?.runs)
        ? envelope.runs
        : Array.isArray(conversation?.runs)
            ? conversation.runs
            : [];

    return {
        conversation: {
            id: conversation?.id || envelope?.conversationId || null,
            title: normalizeText(conversation?.title || envelope?.title || '').trim(),
            metadata: isObject(conversation?.metadata) ? conversation.metadata : {},
            loaded: true,
        },
        messages: messages.map((message) => normalizeMessage(message, {metadata: {source: 'persisted'}})),
        runs: runs.map((run) => normalizeRun(run)),
    };
}

export function buildOptimisticMessage({text}) {
    const createdAt = new Date().toISOString();
    return {
        id: createLocalId('optimistic'),
        role: 'user',
        text: normalizeText(text).trim(),
        createdAt,
        status: 'pending',
        runId: null,
        metadata: {
            source: 'optimistic',
        },
    };
}

export function findMatchingOptimisticUserMessage(state, message) {
    const targetText = normalizeText(message?.text).trim();
    if (!targetText) {
        return null;
    }

    return state.messageOrder.find((id) => {
        const candidate = state.messagesById[id];
        return candidate?.role === 'user'
            && candidate?.metadata?.source === 'optimistic'
            && normalizeText(candidate?.text).trim() === targetText;
    }) || null;
}

export function extractMessagePayload(payload = {}) {
    const envelope = extractConversationEnvelope(payload);
    if (typeof envelope?.message === 'string') {
        return envelope.message;
    }
    if (isObject(envelope?.message)) {
        return envelope.message;
    }
    if (typeof envelope?.data?.message === 'string') {
        return envelope.data.message;
    }
    if (isObject(envelope?.data?.message)) {
        return envelope.data.message;
    }
    if (typeof envelope?.text === 'string') {
        return envelope.text;
    }
    if (isObject(envelope) && (envelope.id || envelope.messageId)) {
        return envelope;
    }
    return null;
}

export function extractRunPayload(payload = {}) {
    const envelope = extractConversationEnvelope(payload);
    if (isObject(envelope?.run)) {
        return envelope.run;
    }
    if (isObject(envelope?.data?.run)) {
        return envelope.data.run;
    }
    if (isObject(envelope) && (envelope.id || envelope.runId)) {
        return envelope;
    }
    return null;
}

export function extractRunId(payload = {}) {
    const run = extractRunPayload(payload);
    if (run?.id || run?.runId) {
        return run.id || run.runId;
    }

    const envelope = extractConversationEnvelope(payload);
    return envelope?.runId || envelope?.data?.runId || envelope?.message?.runId || null;
}

export function extractMessageId(payload = {}) {
    const message = extractMessagePayload(payload);
    if (typeof message === 'object' && (message?.id || message?.messageId)) {
        return message.id || message.messageId;
    }

    const envelope = extractConversationEnvelope(payload);
    return envelope?.messageId
        || envelope?.data?.messageId
        || envelope?.message?.id
        || envelope?.message?.messageId
        || null;
}

export function extractStepId(payload = {}) {
    const envelope = extractConversationEnvelope(payload);
    return envelope?.stepId || envelope?.data?.stepId || envelope?.step?.id || envelope?.step?.stepId || null;
}

export function extractAssistantChunkText(payload = {}) {
    const envelope = extractConversationEnvelope(payload);
    const content = isObject(envelope?.content) ? envelope.content : {};
    const stringContent = typeof envelope?.content === 'string' ? envelope.content : '';
    return normalizeText(
        envelope?.message
        ?? envelope?.delta
        ?? envelope?.chunk
        ?? stringContent
        ?? content?.message
        ?? content?.text
        ?? envelope?.text
    );
}

export function deriveEventName(explicitEventName, payload = {}) {
    if (explicitEventName && explicitEventName !== 'message') {
        return explicitEventName;
    }

    const envelope = extractConversationEnvelope(payload);
    return envelope?.type || envelope?.event || envelope?.name || explicitEventName || 'message';
}

export function isConversationNotFoundError(error) {
    const message = normalizeText(error?.message).toLowerCase();
    return message.includes('conversation not found') || message.includes('conversation_not_found');
}

/**
 * Load failures where the persisted thread id should be cleared instead of sticking an error
 * banner: missing conversations (404) and gateway/upstream outages (502/503/504) while dash-api
 * is restarting — Caddy returns those when the upstream dial fails.
 */
export function isRecoverableConversationLoadError(error) {
    if (isConversationNotFoundError(error)) {
        return true;
    }
    const message = normalizeText(error?.message);
    return /\b(404|502|503|504)\b/.test(message);
}

